import { RestTimezone } from '@main-application/management/pages/system-configuration/components/date-time-configuration/model/timezone';
import { environment } from 'environments/environment';
import { EnvironmentType } from 'environments/environment-config';

import { TimezoneHelper } from './timezone.helper';

export type TimezoneConversionFunction<T> = (data: T, timezone: RestTimezone) => T;

export class TimezoneEntityHelperCommon {
  /**
   * Change timezone for date feilds of data before sending to server
   *
   * @param data - object
   * @param timezone - local timezone of Client app
   * @param vanilaDates - array of fields that should contain vanila date without time part
   * @param nestedObjects - array of fields that contain complicated objects that should use custom function for field correction
   */
  public static readonly fixTimezoneForModelToServer = <T>(
    data: T,
    timezone: RestTimezone,
    vanilaDates: (keyof T)[] = [],
    nestedObjects: { field: keyof T; f: TimezoneConversionFunction<any> }[] = []
  ) => {
    if (!data || (typeof data !== 'object' && !Array.isArray(data))) {
      return data;
    }
    const dataWithoutTimezone = Array.isArray(data) ? ([...data] as T) : { ...data };
    for (const field in dataWithoutTimezone) {
      if (nestedObjects.some(e => e.field == field)) {
        dataWithoutTimezone[field] = nestedObjects
          .find(e => e.field == field)
          .f(dataWithoutTimezone[field], timezone) as any;
      } else if (vanilaDates.includes(field)) {
        dataWithoutTimezone[field] = TimezoneHelper.removeTimeZone(dataWithoutTimezone[field] as Date) as any;
      } else if (
        dataWithoutTimezone[field] instanceof Date ||
        this.isStringLikeServerDate(dataWithoutTimezone[field])
      ) {
        dataWithoutTimezone[field] = TimezoneHelper.replaceTimezoneToServer(
          dataWithoutTimezone[field] as Date,
          timezone
        ) as any;
      } else if (typeof dataWithoutTimezone[field] === 'object') {
        dataWithoutTimezone[field] = this.fixTimezoneForModelToServer(dataWithoutTimezone[field], timezone, [], []);
      }
    }
    return dataWithoutTimezone;
  };

  /**
   * Change timezone for date feilds of data that was just received from server
   *
   * @param data - object
   * @param timezone - local timezone of Client app
   * @param vanilaDates - array of fields that should contain vanilla date without time part
   * @param nestedObjects - array of fields that contain complicated objects that should use custom function for field correction
   */

  private static readonly loggedFields = new Set<string>();

  public static readonly fixTimezoneForModelToClient = <T>(
    data: T,
    timezone: RestTimezone,
    vanilaDates: (keyof T)[] = [],
    nestedObjects: { field: keyof T; f: TimezoneConversionFunction<any> }[] = []
  ) => {
    if (!data) return data;
    for (const field in data) {
      if (nestedObjects.some(e => e.field == field)) {
        data[field] = nestedObjects.find(e => e.field == field).f(data[field], timezone) as any;
      } else if (vanilaDates.includes(field)) {
        if (this.isDateOnlyFormat(data[field] as string)) {
          data[field] = new Date(data[field] as Date) as any;
        } else if (this.isDateTimeWithoutOffset(data[field] as string)) {
          data[field] = new Date(data[field] as Date) as any;
        } else {
          data[field] = TimezoneHelper.addTimeZoneAndKeepOnlyDatePart(data[field] as Date) as any;
        }
      } else if (this.isDateOnlyFormat(data[field] as string)) {
        const value = data?.[field];

        if (
          environment.type !== EnvironmentType.Prod &&
          environment.type !== EnvironmentType.Demo &&
          field &&
          typeof value !== 'object' &&
          !this.loggedFields.has(field)
        ) {
          this.loggedFields.add(field);
          console.warn('Field is not in vanilaDates', field, value);
        }

        data[field] = new Date(data[field] as Date) as any;
      } else if (data[field] instanceof Date || this.isStringLikeServerDate(data[field])) {
        data[field] = TimezoneHelper.replaceTimezoneToClient(data[field] as Date, timezone) as any;
      } else if (typeof data[field] === 'object') {
        this.fixTimezoneForModelToClient(data[field], timezone, vanilaDates as any[], []);
      }
    }
    return data;
  };

  private static isStringLikeServerDate(dateString: any) {
    return (
      typeof dateString === 'string' &&
      /^[0-9]{4}-[01][0-9]-[0-3][0-9]T[0-5][0-9]:[0-5][0-9]:[0-5][0-9](\.[0-9][0-9]?)?Z$/.test(dateString)
      //Valid examples of server date string:
      //2023-07-06T00:00:00Z
      //2023-07-06T09:37:48.2Z
      //2023-07-06T09:37:21.85Z
    );
  }

  private static isDateOnlyFormat(dateString: any) {
    return typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?$/.test(dateString);
    //Valid examples
    //2025-01-01T00:00:00
    //2025-01-01T00:00:00.000
  }

  private static isDateTimeWithoutOffset(dateString: any) {
    return typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,7})?$/.test(dateString);
    //Valid examples
    //2025-01-01T01:00:00
    //2025-01-01T13:30:00.000
    //2025-01-01T23:30:50.000
  }
}
